import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography,
  FormControl, InputLabel, Select, MenuItem,
  DialogContentText, Alert, IconButton, Chip,
  List, ListItem, ListItemText,
  ThemeProvider, createTheme
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { useFormError } from '../../../components/Datatable/FormErrorProvider';
import { useParams } from 'react-router-dom';
import { extractDomainsFromText, REASON_LABELS } from '../../../utils/domainFilter';

export default function DomainForm({ open, id, categories, onClose, onSubmit }) {
  const error = useFormError();
  const fileInputRef = useRef(null);
  const { categoryId} = useParams();
  const [formData, setFormData] = useState({
    blacklist: '',
    categoryId: categoryId,
    file: null
  });
  
  const [fileError, setFileError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, valid: [], rejected: [], essential: [] });

  // Reset form data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        blacklist: '',
        categoryId: categoryId,
        file: null
      });
      setFileError('');
      error.setErrors([]);
    }
  }, [open, categories]);

  // Handle text field changes — sem filtro restritivo no input.
  // O parser/filtro (extractDomainsFromText) já limpa protocolo, path, etc.
  const onChangeDomains = (event) => {
    setFormData({ ...formData, blacklist: event.target.value });
  };

  // Lê o clipboard e anexa ao campo. Útil pra colar listas grandes.
  const handlePasteFromClipboard = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.readText) {
        setFileError('Seu navegador não permite acesso à área de transferência.');
        return;
      }
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) {
        setFileError('Área de transferência vazia.');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        blacklist: prev.blacklist ? `${prev.blacklist}\n${text}` : text,
      }));
      setFileError('');
    } catch (e) {
      setFileError('Falha ao ler área de transferência. Cole manualmente (Ctrl+V).');
    }
  };

  // Handle category selection
  const onChangeCategory = (event) => {
    setFormData({ ...formData, categoryId: event.target.value });
  };

  // Handle file upload
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFileError('');
    
    if (!file) return;
    
    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setFileError('O arquivo não pode ter mais de 5MB');
      return;
    }
    
    // Check file type
    const validTypes = ['text/plain', 'text/csv', 'application/csv', 'application/vnd.ms-excel'];
    if (!validTypes.includes(file.type)) {
      setFileError('Apenas arquivos de texto (.txt) ou CSV (.csv) são permitidos');
      return;
    }
    
    setFormData({ ...formData, file });
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setFormData({ ...formData, file: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input click
  const handleClickUpload = () => {
    fileInputRef.current.click();
  };

  // Constrói o FormData final e dispara o submit ao backend
  const buildAndSubmit = (textOverride = null) => {
    const submitData = new FormData();
    submitData.append('category', formData.categoryId);
    const text = textOverride !== null ? textOverride : formData.blacklist.trim();
    if (text) {
      submitData.append('blacklist', text);
    }
    if (formData.file) {
      submitData.append('file', formData.file);
    }
    onSubmit(submitData);
  };

  // Form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setFileError('');

    // Validate
    if (!formData.blacklist.trim() && !formData.file) {
      error.setErrors([{ field: "blacklist", message: "Lista de domínios ou arquivo não pode estar vazio" }]);
      return;
    }

    // Validação client-side só do texto colado (arquivo é validado no backend).
    if (formData.blacklist.trim()) {
      const { valid, rejected, essential } = extractDomainsFromText(formData.blacklist);
      if (rejected.length > 0 || essential.length > 0) {
        setConfirmDialog({ open: true, valid, rejected, essential });
        return; // espera confirmação do usuário
      }
    }

    buildAndSubmit();
  };

  const handleConfirmContinue = () => {
    const text = confirmDialog.valid.join('\n');
    setConfirmDialog({ open: false, valid: [], rejected: [], essential: [] });
    buildAndSubmit(text);
  };

  const handleConfirmCancel = () => {
    setConfirmDialog({ open: false, valid: [], rejected: [], essential: [] });
  };

  return (
    <ThemeProvider theme={createTheme({
      typography: {
        fontFamily: [
          'sans-serif',
          'Roboto',
          'Montserrat',
        ]
      },
      palette: {
        mode: 'light',
        primary: {
          main: "#e01b29",
        },
      }
    })}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {categoryId ? "Adicionar Domínios a:" + ` ${categories?.find(cat => cat.id == categoryId)?.name}` : "Adicionar Domínios"}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Digite os domínios que deseja adicionar à lista de bloqueio ou faça upload de um arquivo.
              Você pode inserir um domínio por linha ou separados por vírgula.
            </DialogContentText>
            {/* { !categoryId && 
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="category-select-label">Categoria</InputLabel>
              <Select
                labelId="category-select-label"
                value={formData.categoryId}
                label="Categoria"
                onChange={onChangeCategory}
              >
                {categories?.map((category) => (
                  <MenuItem
                    key={category.id}
                    value={category.id}
                    disabled={!category.active}
                  >
                    {category.name} {!category.active && "(Inativa)"}
                  </MenuItem>
                ))} */}
              {/* </Select>
            </FormControl>} */}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentPasteIcon />}
                onClick={handlePasteFromClipboard}
              >
                Colar da área de transferência
              </Button>
            </Box>

            <TextField
              multiline
              rows={10}
              fullWidth
              error={error.verify("blacklist")}
              helperText={error.msgError("blacklist") || "Digite um domínio por linha ou separados por vírgula. URLs (https://...) e protocolos são limpos automaticamente."}
              label='Domínios'
              name="blacklist"
              value={formData.blacklist}
              onChange={onChangeDomains}
              placeholder="exemplo.com.br
outro-exemplo.com
terceiro.exemplo.net"
              sx={{ mb: 3 }}
            />

            <Box sx={{ mt: 2, mb: 2, border: '1px dashed #ccc', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Ou faça upload de um arquivo (.txt ou .csv)
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleClickUpload}
                >
                  Selecionar arquivo
                </Button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                
                {formData.file && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>
                      {formData.file.name} ({(formData.file.size / 1024).toFixed(2)} KB)
                    </Typography>
                    <IconButton size="small" onClick={handleRemoveFile}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Tamanho máximo: 5MB. Formatos permitidos: TXT, CSV.
              </Typography>
              
              {fileError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {fileError}
                </Alert>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">
              {id ? 'Atualizar' : 'Adicionar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmação: rejeitados (gov/inválido) + aviso de essenciais */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmCancel}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Confirmar cadastro
        </DialogTitle>
        <DialogContent dividers>
          {confirmDialog.rejected.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{confirmDialog.rejected.length}</strong> domínio(s) <strong>não serão cadastrados</strong> por serem governamentais ou inválidos:
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
                {confirmDialog.rejected.map((r, i) => (
                  <ListItem key={`rej-${r.domain}-${i}`} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={r.domain}
                      secondary={REASON_LABELS[r.reason] || r.reason}
                    />
                    <Chip
                      size="small"
                      label={r.reason === 'government' ? 'gov' : 'inválido'}
                      color={r.reason === 'government' ? 'error' : 'warning'}
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {confirmDialog.essential.length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff8e1', border: '1px solid #ffb300', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#b26a00', fontWeight: 600 }}>
                ⚠ Atenção: {confirmDialog.essential.length} domínio(s) amplamente utilizado(s)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                Bloquear esses domínios pode impactar o trabalho dos usuários.
                Tem certeza que deseja prosseguir?
              </Typography>
              <List dense sx={{ maxHeight: 160, overflow: 'auto', bgcolor: '#fffdf5', borderRadius: 1 }}>
                {confirmDialog.essential.map((d, i) => (
                  <ListItem key={`ess-${d}-${i}`} sx={{ py: 0.25 }}>
                    <ListItemText primary={d} />
                    <Chip size="small" label="essencial" color="warning" />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Typography variant="body2">
            Continuar e cadastrar os <strong>{confirmDialog.valid.length}</strong> domínio(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel}>Cancelar</Button>
          <Button
            onClick={handleConfirmContinue}
            variant="contained"
            color={confirmDialog.essential.length > 0 ? 'warning' : 'primary'}
            disabled={confirmDialog.valid.length === 0}
          >
            {confirmDialog.essential.length > 0 ? 'Bloquear mesmo assim' : 'Continuar'}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}